/**
 * Utility functions for handling images from Drupal backend
 */

import type { JsonApiResource, JsonApiResponse } from 'next-drupal';

const backendBase = process.env.NEXT_PUBLIC_DRUPAL_BASE_URL || '';
const solutionsBackendBase = process.env.NEXT_PUBLIC_SOLUTIONS_BACKEND_BASE_URL || '';

/**
 * Convert a relative path to absolute URL using backend base
 * @param src - The source URL (relative or absolute)
 * @param useSolutionsBackend - If true, use SOLUTIONS backend instead of DRUPAL backend
 */
export function toAbsoluteUrl(src: string | undefined | null, useSolutionsBackend: boolean = false): string {
  if (!src) return '';
  if (/^https?:\/\//i.test(src)) return src;
  
  const base = useSolutionsBackend ? solutionsBackendBase : backendBase;
  if (!base) return src;
  
  // If the path starts with /images/ or /icons/, treat it as a static public file
  // These are served by Next.js and should NOT be converted to Drupal URLs
  if (src.startsWith('/images/') || src.startsWith('/icons/')) {
    return src;
  }
  
  const path = src.startsWith('/') ? src : `/${src}`;
  return `${base}${path}`;
}

/**
 * Extract image URL from Drupal JSON:API file relationship
 */
export function extractImageUrl(
  imageRelationship: { data?: { id?: string } } | null | undefined,
  included: JsonApiResource[] = []
): string | null {
  if (!imageRelationship?.data?.id) return null;

  // Find the file in included data
  const fileData = included.find(
    item => item.type === 'file--file' && item.id === imageRelationship.data?.id
  );

  const uriData = (fileData?.attributes as Record<string, unknown>)?.uri as
    | { url?: string }
    | string
    | undefined;
  const uriUrl: string | undefined = typeof uriData === 'string' ? uriData : uriData?.url;
  if (!uriUrl) return null;

  return toAbsoluteUrl(uriUrl);
}

/**
 * Build background image URL for CSS background-image property
 */
export function buildBackgroundImageUrl(
  imageUrl: string | null | undefined,
  fallbackUrl: string = '/images/default-theme.jpg'
): string {
  if (!imageUrl) {
    return `url(${fallbackUrl})`;
  }

  // For absolute URLs, use directly
  if (/^https?:\/\//i.test(imageUrl)) {
    return `url(${imageUrl})`;
  }

  // For relative paths from Drupal, convert to absolute
  const absoluteUrl = toAbsoluteUrl(imageUrl);
  return `url(${absoluteUrl})`;
}

/**
 * Extract theme image URL from JSON:API theme data
 */
export function extractThemeImageUrl(
  themeData: JsonApiResponse,
  included?: JsonApiResource[]
): string | null {
  const imageRelationship = themeData?.relationships?.field_theme_image;
  return extractImageUrl(imageRelationship, included);
}
